/**
 * ModuleOrchestrationService  —  The "Bridge" between platform modules and Menu Profiles.
 *
 * When a connected module (e.g. Aggregator) pushes items, this service:
 * 1. Finds all Menu Profiles for the target Brand where `connectedModules` includes the caller.
 * 2. Auto-creates an "OFFERS" category if one doesn't exist.
 * 3. Injects items as `systemManaged` — locked name/price in the Menu UI.
 * 4. Removes stale system-managed items whose sourceEntityId is no longer active.
 *
 * Architecture: Pure function + repository calls — no UI dependency.
 */

import { getRepository } from '../../../config/repository';
import { PLATFORM_MODULES } from '../../../core/appRegistry';
import type { MenuProfile, OptionGroup } from '../types';
import { calcPromoPrice } from './PromoService';
import { apiFetch } from '../../../data/ApiModuleRepository';

// ─── Types ────────────────────────────────────────────────────────────────────

/** A single item a source module wants to inject into connected menus. */
export interface InjectedItem {
  /** Unique ID from the source module (e.g. campaign-item ID). */
  sourceEntityId: string;
  nameEN: string;
  nameAR?: string;
  descriptionEN?: string;
  descriptionAR?: string;
  basePrice: number;
  image?: string;
  /** Deep-copied option groups from the original menu item */
  optionGroups?: OptionGroup[];
  /** Campaign promotion metadata for strikethrough pricing & lifecycle */
  campaignMeta?: {
    campaignId: string;
    campaignName: string;
    discountValue: number;
    discountType: string;
    fundingType: string;
    sterlingSharePct?: number;
    partnerSharePct?: number;
    startDate: number;
    endDate: number;
  };
}

export interface OrchestrationResult {
  profileId: string;
  profileName: string;
  itemsInjected: number;
  itemsRemoved: number;
  offersCreated: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getModuleName(moduleId: string): string {
  return PLATFORM_MODULES.find(m => m.id === moduleId)?.name ?? moduleId;
}

// ─── Core Orchestration ───────────────────────────────────────────────────────

/**
 * Push items from a source module into all connected Menu Profiles for a brand.
 *
 * Uses the dedicated /inject endpoint so ONLY the OFFERS category is touched —
 * all other categories and items are preserved server-side.
 */
export async function pushItemsToConnectedMenus(
  callingModuleId: string,
  brandId: string,
  items: InjectedItem[],
  activeEntityIds: string[],
): Promise<OrchestrationResult[]> {
  const repo = getRepository();
  const allProfiles = await repo.getAll<MenuProfile>('menu-profiles');

  const safeArr = (v: unknown): string[] => { if (Array.isArray(v)) return v; if (typeof v === 'string') { try { const p = JSON.parse(v); if (Array.isArray(p)) return p; } catch {} } return []; };

  // Find profiles for this brand that have the calling module connected
  const targets = allProfiles.filter(
    p => p.brandId === brandId && safeArr(p.connectedModules).includes(callingModuleId),
  );

  const results: OrchestrationResult[] = [];

  for (const profile of targets) {
    // Build injection payload with deep-cloned data + calculated promo prices
    const injectionItems = items.map(incoming => ({
      sourceEntityId: incoming.sourceEntityId,
      nameEN: incoming.nameEN,
      nameAR: incoming.nameAR || '',
      descriptionEN: incoming.descriptionEN,
      descriptionAR: incoming.descriptionAR,
      basePrice: incoming.basePrice,
      image: incoming.image,
      optionGroups: incoming.optionGroups
        ? JSON.parse(JSON.stringify(incoming.optionGroups))
        : [],
      campaignMeta: incoming.campaignMeta ? {
        ...incoming.campaignMeta,
        originalPrice: incoming.basePrice,
        promoPrice: calcPromoPrice(incoming.basePrice, incoming.campaignMeta.discountValue, incoming.campaignMeta.discountType),
      } : undefined,
    }));

    try {
      const res = await apiFetch<{ data: { profileId: string; injected: number; removed: number; totalItems: number } }>(
        `/api/menu/profiles/${profile.id}/inject`,
        {
          method: 'POST',
          body: JSON.stringify({
            callingModule: callingModuleId,
            items: injectionItems,
            activeEntityIds,
          }),
        },
      );

      results.push({
        profileId: profile.id,
        profileName: profile.name,
        itemsInjected: res.data.injected,
        itemsRemoved: res.data.removed,
        offersCreated: false,
      });
    } catch (err) {
      console.error(`[Orchestration] Injection failed for profile "${profile.name}" (${profile.id}):`, err);
      results.push({
        profileId: profile.id,
        profileName: profile.name,
        itemsInjected: 0,
        itemsRemoved: 0,
        offersCreated: false,
      });
    }
  }

  return results;
}

/**
 * Remove all system-managed items from a module across all connected menus for a brand.
 * Uses the dedicated /revoke endpoint for surgical removal.
 */
export async function removeModuleItems(
  callingModuleId: string,
  brandId: string,
  entityIds?: string[],
): Promise<number> {
  const repo = getRepository();
  const allProfiles = await repo.getAll<MenuProfile>('menu-profiles');
  const safeArr = (v: unknown): string[] => { if (Array.isArray(v)) return v; if (typeof v === 'string') { try { const p = JSON.parse(v); if (Array.isArray(p)) return p; } catch {} } return []; };
  const targets = allProfiles.filter(
    p => p.brandId === brandId && safeArr(p.connectedModules).includes(callingModuleId),
  );

  let totalRemoved = 0;

  for (const profile of targets) {
    try {
      const res = await apiFetch<{ data: { profileId: string; removed: number } }>(
        `/api/menu/profiles/${profile.id}/revoke`,
        {
          method: 'POST',
          body: JSON.stringify({ callingModule: callingModuleId }),
        },
      );
      totalRemoved += res.data.removed;
    } catch (err) {
      console.error(`[Orchestration] removeModuleItems failed for profile "${profile.name}" (${profile.id}):`, err);
    }
  }

  return totalRemoved;
}

/**
 * Revoke all injected items matching a specific campaignId from ALL menu profiles.
 * Uses the dedicated /revoke endpoint so only campaign items are removed —
 * all other categories and items are preserved server-side.
 */
export async function revokeCampaignItems(campaignId: string): Promise<number> {
  const repo = getRepository();
  const allProfiles = await repo.getAll<MenuProfile>('menu-profiles');
  let totalRemoved = 0;

  for (const profile of allProfiles) {
    // Check if this profile even has campaign items to avoid unnecessary API calls
    const hasCampaignItems = profile.categories?.some(cat =>
      cat.items?.some(item => item.campaignMeta?.campaignId === campaignId),
    );
    if (!hasCampaignItems) continue;

    try {
      const res = await apiFetch<{ data: { profileId: string; removed: number } }>(
        `/api/menu/profiles/${profile.id}/revoke`,
        {
          method: 'POST',
          body: JSON.stringify({ campaignId, callingModule: 'aggregator' }),
        },
      );
      totalRemoved += res.data.removed;
    } catch (err) {
      console.error(`[Orchestration] Revoke failed for profile "${profile.name}" (${profile.id}):`, err);
    }
  }

  return totalRemoved;
}
